/**
 * Customers.new Widget SDK — Public API
 *
 * Usage:
 *   <script src="https://cdn.igreet.ai/widget/igreet-widget.umd.js"></script>
 *   <script>iGreetWidget.init({ appId: "app_abc123" });</script>
 */
import { Widget } from "./widget.js";
import { bind as binderBind, bindForm as binderBindForm } from "./binder.js";
import { HeadlessClient } from "./headless.js";
import { SessionRecorder } from "./replay.js";
import { SurveyManager } from "./survey.js";
import { logger } from "./logger.js";

let _instance = null;

/**
 * Initialize the Customers.new widget.
 * @param {Object} options
 * @param {string} options.appId        — Widget app ID from the admin dashboard
 * @param {string} [options.configUrl]  — Override config API base URL
 * @param {string} [options.tokenUrl]   — Override token API base URL
 * @param {object} [options.journey]    — Journey tracker options
 * @param {boolean} [options.journey.enabled=true]  — Enable/disable tracking
 * @param {boolean} [options.journey.trackSections=true] — Section scroll tracking
 * @param {string}  [options.journey.sectionSelector]    — CSS selector for sections
 * @param {number}  [options.journey.sectionThreshold=0.4] — Visibility threshold
 * @param {string}  [options.journey.spa="auto"]   — SPA mode: "auto"|"true"|"false"
 * @param {boolean} [options.journey.hashNav=true]  — Treat hash changes as navigation
 * @returns {Widget}
 */
export function init(options = {}) {
    if (!options.appId) {
        throw new Error("[iGreet] appId is required. Get it from your Customers.new dashboard.");
    }

    if (_instance) {
        logger.warn("[iGreet] Widget already initialized. Destroying previous instance.");
        _instance.destroy();
        _instance = null;
    }

    _instance = new Widget(options);
    _instance._onDestroy = () => { _instance = null; };
    _instance.mount();
    return _instance;
}

/**
 * Destroy the current widget instance.
 */
export function destroy() {
    if (_instance) {
        _instance.destroy();
        _instance = null;
    }
}

/**
 * Universal bind function supporting both options object ({ appId, server, elements })
 * and legacy 2-argument signature (selectors, options).
 */
export function bind(selectorsOrOptions = {}, maybeOptions = {}) {
    if (selectorsOrOptions && (selectorsOrOptions.elements || selectorsOrOptions.appId || selectorsOrOptions.server || !maybeOptions || Object.keys(maybeOptions).length === 0)) {
        return binderBind(selectorsOrOptions);
    }

    const selectors = selectorsOrOptions;
    const options = maybeOptions;
    const widget = init({ ...options, mode: "headless" });

    widget._boundElements = [];

    const addListener = (el, event, handler) => {
        const target = typeof el === "string" ? document.querySelector(el) : el;
        if (!target) return;
        target.addEventListener(event, handler);
        widget._boundElements.push({ target, event, handler });
    };

    if (selectors.openBtn) {
        addListener(selectors.openBtn, "click", () => widget.open());
    }
    if (selectors.closeBtn) {
        addListener(selectors.closeBtn, "click", () => widget.close());
    }
    if (selectors.toggleBtn) {
        addListener(selectors.toggleBtn, "click", () => widget.toggle());
    }
    if (selectors.sendBtn && selectors.inputField) {
        const input = typeof selectors.inputField === "string"
            ? document.querySelector(selectors.inputField)
            : selectors.inputField;
        addListener(selectors.sendBtn, "click", () => {
            if (input && input.value.trim()) {
                widget.sendTextMessage(input.value.trim());
                input.value = "";
            }
        });
        if (input) {
            addListener(input, "keydown", (e) => {
                if (e.key === "Enter" && !e.shiftKey && input.value.trim()) {
                    e.preventDefault();
                    widget.sendTextMessage(input.value.trim());
                    input.value = "";
                }
            });
        }
    }
    if (selectors.voiceToggleBtn) {
        addListener(selectors.voiceToggleBtn, "click", () => {
            if (widget.state === "voice") {
                widget.switchToText();
            } else {
                widget.switchToVoice();
            }
        });
    }

    return widget;
}

export function bindForm(options = {}) {
    return binderBindForm(options);
}

export { HeadlessClient } from "./headless.js";

/**
 * Initialize headless mode without any UI
 */
export function initHeadless(options = {}) {
    const client = new HeadlessClient(options);
    client.init?.();
    return client;
}

export function initReplay(options = {}) {
    const recorder = new SessionRecorder(options);
    recorder.start();
    return recorder;
}

export function initSurveys(options = {}) {
    const manager = new SurveyManager(options);
    manager.init();
    return manager;
}

export function group(type, key, props) {
    if (_instance) _instance.group(type, key, props);
}

export { SurveyManager, SessionRecorder };

// ── Auto-init from <script data-app-id="..." data-server="..."> ──
(function autoInit() {
    // Find the script tag that loaded this file
    const currentScript = document.currentScript
        || document.querySelector("script[data-app-id]");
    if (!currentScript) return;

    const appId = currentScript.getAttribute("data-app-id");
    if (!appId) return;

    let serverUrl = currentScript.getAttribute("data-server") || "";
    if (!serverUrl && currentScript.src) {
        try {
            const urlObj = new URL(currentScript.src, window.location.href);
            serverUrl = urlObj.origin;
        } catch {}
    }
    if (!serverUrl) {
        serverUrl = window.location.origin;
    }

    const mode = currentScript.getAttribute("data-mode") || "floating";
    const target = currentScript.getAttribute("data-target") || "";

    // If explicit headless mode attribute is present on script tag without manual init, skip default shell
    if (mode === "headless") return;

    // Wait for DOM to be ready, then auto-init
    const doInit = () => {
        init({
            appId,
            configUrl: serverUrl,
            serverUrl: serverUrl,
            server: serverUrl,
            tokenUrl: serverUrl,
            target,
            mode,
        });
    };

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", doInit);
    } else {
        doInit();
    }
})();
